<template>
  <main class="cover-auth">
    <div class="widget-auth">
      <div class="text-center mb-8">
        <img src="/public/images/logo-wide.svg" class="auth-logo2" />
      </div>

      <h2 class="font-semibold text-center my-6">Vérification de votre email</h2>
      
      <p class="text-center text-grey-600 mb-6">
        Nous avons envoyé un code de vérification à <br/>
        <strong>{{ userEmail }}</strong>
      </p>

      <form class="form" @submit.prevent="handleSubmit">
        <div class="flex gap-3 mb-7 justify-center">
          <input 
            v-for="(digit, index) in otpCode" 
            :key="index"
            type="text" 
            class="form-control-code" 
            :id="`otp-${index}`"
            v-model="otpCode[index]"
            maxlength="1"
            @input="handleInput(index, $event)"
            @keydown="handleKeydown(index, $event)"
            @paste="handlePaste"
            placeholder=""
            inputmode="numeric"
            pattern="[0-9]"
          >
        </div>

        <button 
          type="submit" 
          class="btn lg w-100 flex-center mt-5 mb-4"
          :disabled="!isCodeComplete || isSubmitting"
        >
          <img src="/public/icons/icon-valider.svg" class="btn-icon" alt="" />
          <span>{{ isSubmitting ? 'Vérification...' : 'Vérifier' }}</span>
        </button>

        <button 
          type="button"
          @click="resendCode" 
          class="btn lg w-100 flex-center btn-outline"
          :disabled="isResending"
        >
          <span>{{ isResending ? 'Envoi en cours...' : 'Renvoyer le code' }}</span>
        </button>
      </form>

      <p class="text-read text-grey-600 text-center mt-5">
        Vérifiez votre boîte de réception et vos spams si vous ne voyez pas l'email.
      </p>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent, reactive, computed, nextTick, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import authService from '@/services/authService'
import { showAlert } from '@/utils/swal'

export default defineComponent({
  name: 'AuthOtp',
  setup() {
    const router = useRouter()
    const otpCode = reactive<string[]>(['', '', '', '', '', ''])
    const isSubmitting = ref(false)
    const isResending = ref(false)
    const userEmail = ref('')

    // Vérifier que les données d'inscription sont présentes
    onMounted(() => {
      // Vérifier d'abord si l'email est dans l'URL (depuis le lien du mail)
      const route = router.currentRoute.value
      const emailFromUrl = route.query.email as string | undefined
      
      if (emailFromUrl) {
        userEmail.value = decodeURIComponent(emailFromUrl)
        return
      }
      
      // Sinon, vérifier dans sessionStorage
      const registerDataStr = sessionStorage.getItem('registerData')
      if (!registerDataStr) {
        // Pas de données d'inscription, rediriger vers signup
        router.push('/auth/sign-up')
        return
      }

      const registerData = JSON.parse(registerDataStr)
      userEmail.value = registerData.email
    })

    const isCodeComplete = computed(() => {
      return otpCode.every(digit => digit !== '' && /^\d$/.test(digit))
    })

    const focusInput = (index: number) => {
      nextTick(() => {
        const input = document.getElementById(`otp-${index}`) as HTMLInputElement
        if (input) {
          input.focus()
        }
      })
    }

    const handleInput = (index: number, event: Event) => {
      const target = event.target as HTMLInputElement
      const value = target.value

      // Ne garder que les chiffres
      if (!/^\d*$/.test(value)) {
        otpCode[index] = ''
        return
      }

      // Si on entre un chiffre, passer au champ suivant
      if (value.length === 1 && index < 5) {
        focusInput(index + 1)
      }
    }

    const handleKeydown = (index: number, event: KeyboardEvent) => {
      // Gérer la touche Backspace
      if (event.key === 'Backspace' && otpCode[index] === '' && index > 0) {
        event.preventDefault()
        focusInput(index - 1)
      }

      // Gérer les flèches gauche/droite
      if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        focusInput(index - 1)
      }

      if (event.key === 'ArrowRight' && index < 5) {
        event.preventDefault()
        focusInput(index + 1)
      }
    }

    const handlePaste = (event: ClipboardEvent) => {
      event.preventDefault()
      const pastedData = event.clipboardData?.getData('text')
      
      if (pastedData && /^\d{6}$/.test(pastedData)) {
        for (let i = 0; i < 6; i++) {
          otpCode[i] = pastedData[i]
        }
        focusInput(5)
      }
    }

    const handleSubmit = async () => {
      if (!isCodeComplete.value) {
        await showAlert.warning('Veuillez saisir le code de vérification à 6 chiffres')
        return
      }

      isSubmitting.value = true

      try {
        const code = otpCode.join('')
        
        // Appel à l'API de vérification du code OTP
        const response = await authService.checkEmail(code)

        if (response.status === 0) {
          // Succès - Email vérifié
          await showAlert.success('Email vérifié avec succès !')
          
          // Rediriger vers la page de création du code de sécurité
          router.push('/auth/sign-up-activation')
        } else {
          // Erreur
          await showAlert.error(response.message || 'Code incorrect')
        }
      } catch (error) {
        console.error('Erreur lors de la vérification:', error)
        await showAlert.error('Une erreur est survenue. Veuillez réessayer.')
      } finally {
        isSubmitting.value = false
      }
    }

    const resendCode = async () => {
      if (isResending.value) return

      isResending.value = true

      try {
        // Appel à l'API pour renvoyer le code
        const response = await authService.verifyEmail()

        if (response.status === 0) {
          await showAlert.success('Un nouveau code a été envoyé à votre adresse email !')
          // Réinitialiser les champs
          otpCode.forEach((_, index) => otpCode[index] = '')
          focusInput(0)
        } else {
          await showAlert.error(response.message || 'Erreur lors de l\'envoi du code')
        }
      } catch (error) {
        console.error('Erreur lors du renvoi:', error)
        await showAlert.error('Une erreur est survenue. Veuillez réessayer.')
      } finally {
        isResending.value = false
      }
    }

    return {
      otpCode,
      userEmail,
      isCodeComplete,
      isSubmitting,
      isResending,
      handleInput,
      handleKeydown,
      handlePaste,
      handleSubmit,
      resendCode
    }
  }
})
</script>

<style scoped>
.form-control-code {
  width: 50px;
  height: 60px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.form-control-code:focus {
  outline: none;
  border-color: var(--primary-color, #007bff);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.form-control-code:not(:placeholder-shown) {
  border-color: var(--primary-color, #007bff);
  background-color: #f8f9fa;
}

.flex {
  display: flex;
}

.justify-center {
  justify-content: center;
}

.gap-3 {
  gap: 0.75rem;
}

.text-grey-600 {
  color: #6c757d;
}

.btn-outline {
  background: transparent;
  border: 2px solid var(--primary-color, #007bff);
  color: var(--primary-color, #007bff);
}

.btn-outline:hover {
  background: var(--primary-color, #007bff);
  color: white;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .form-control-code {
    width: 45px;
    height: 50px;
    font-size: 20px;
  }
}
</style>

